<!-- /frontend/src/components/ColumnConfigDialog.vue -->
<template>
  <el-dialog
    :title="dialogTitle"
    v-model="showDialog"
    :width="dialogWidth"
    class="column-config-dialog"
    :close-on-click-modal="false"
    append-to-body
    draggable
  >
    <div class="dialog-content">
      <!-- 保持原有的内容不变 -->
      <div class="config-header">
        <div class="header-left">
          <h3>{{ pageTitle }} - 列设置</h3>
        </div>
        <div class="header-actions">
          <el-button type="primary" link @click="handleResetWidths">
            <el-icon><Refresh /></el-icon> 重置列宽
          </el-button>
          <el-button type="primary" link @click="handleResetAll">
            <el-icon><RefreshRight /></el-icon> 重置所有
          </el-button>
          <el-button type="primary" link @click="handleSelectAll">
            <el-icon><Select /></el-icon> 全选
          </el-button>
          <el-button type="primary" link @click="handleUnselectAll">
            <el-icon><Close /></el-icon> 全不选
          </el-button>
        </div>
      </div>

      <div class="column-list-container">
        <draggable
          v-if="allowColumnReorder"
          v-model="localColumnConfig"
          item-key="prop"
          handle=".drag-handle"
          @end="handleDragEnd"
        >
          <template #item="{ element }">
            <div class="column-item" :class="{ 'column-hidden': !element.visible }">
              <div class="column-controls">
                <el-icon class="drag-handle">
                  <Rank />
                </el-icon>
                <el-checkbox
                  v-model="element.visible"
                  @change="handleColumnChange"
                >
                  {{ element.label }}
                </el-checkbox>
              </div>

              <div class="column-width-control">
                <el-input-number
                  v-model="element.width"
                  :min="30"
                  :max="800"
                  :step="10"
                  controls-position="right"
                  size="small"
                  @change="handleWidthChange(element)"
                />
                <span class="width-unit">px</span>
              </div>

              <el-icon class="visibility-icon" :class="{ hidden: !element.visible }">
                <View v-if="element.visible" />
                <Hide v-else />
              </el-icon>
            </div>
          </template>
        </draggable>
        <template v-else>
          <div
            v-for="element in localColumnConfig"
            :key="element.prop"
            class="column-item"
            :class="{ 'column-hidden': !element.visible }"
          >
            <div class="column-controls">
              <span class="drag-handle-placeholder" aria-hidden="true" />
              <el-checkbox
                v-model="element.visible"
                @change="handleColumnChange"
              >
                {{ element.label }}
              </el-checkbox>
            </div>

            <div class="column-width-control">
              <el-input-number
                v-model="element.width"
                :min="30"
                :max="800"
                :step="10"
                controls-position="right"
                size="small"
                @change="handleWidthChange(element)"
              />
              <span class="width-unit">px</span>
            </div>

            <el-icon class="visibility-icon" :class="{ hidden: !element.visible }">
              <View v-if="element.visible" />
              <Hide v-else />
            </el-icon>
          </div>
        </template>
      </div>

      <div class="config-preview">
        <h4>预览效果</h4>
        <div class="preview-table">
          <div class="preview-header">
            <div 
              v-for="column in visibleColumns" 
              :key="column.prop"
              class="preview-column"
              :style="{ width: column.width + 'px' }"
            >
              {{ column.label }}
            </div>
          </div>
          <div class="preview-row">
            <div 
              v-for="column in visibleColumns" 
              :key="column.prop"
              class="preview-cell"
              :style="{ width: column.width + 'px' }"
            >
              示例数据
            </div>
          </div>
        </div>
      </div>

      <div class="config-tips">
        <el-alert
          :title="configTips"
          type="info"
          :closable="false"
          show-icon
        />
      </div>
    </div>

    <template #footer>
      <span class="dialog-footer">
        <el-button @click="handleCancel">取消</el-button>
        <el-button type="primary" @click="handleSave">应用并保存</el-button>
      </span>
    </template>
  </el-dialog>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { ElMessage } from 'element-plus'
import { getUserScopedStorageKey } from '@/utils/userScopedStorage'
import { 
  Refresh, 
  RefreshRight,
  Select, 
  Close, 
  Rank, 
  View, 
  Hide 
} from '@element-plus/icons-vue'
import draggable from 'vuedraggable'

// 定义 props
const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  columnConfig: {
    type: Array,
    default: () => []
  },
  pageTitle: {
    type: String,
    default: '页面'
  },
  /** 与 getUserScopedStorageKey 拼 `_columns_config` 使用的基键；与 localStorageKey 二选一，优先 localStorageKey */
  storageKey: {
    type: String,
    default: ''
  },
  /** 完整 localStorage 键名（与页面 load/save 列配置时一致）。传入后不再对 storageKey 做二次加用户缀，避免与父组件不一致。 */
  localStorageKey: {
    type: String,
    default: ''
  },
  dialogWidth: {
    type: String,
    default: '600px'
  },
  /** 为 false 时不使用拖拽排序（仅显隐与列宽），避免与固定列表格顺序不一致 */
  allowColumnReorder: {
    type: Boolean,
    default: true
  }
})

// 定义 emits
const emit = defineEmits([
  'update:show',
  'save',
  'reset-widths',
  'reset-all',
  'select-all',
  'unselect-all',
  'config-change'
])

const showDialog = ref(props.show)
const localColumnConfig = ref([...props.columnConfig])

// 计算属性
const dialogTitle = computed(() => `${props.pageTitle} - 列设置`)
const configTips = computed(() => `提示：拖拽调整列顺序，勾选控制显示/隐藏，设置列宽（30-800px）。设置将保存到本地存储。`)

const visibleColumns = computed(() => {
  return localColumnConfig.value.filter(col => col.visible)
})

/** 与父页面读写列配置时必须是同一个键；未传 localStorageKey 时行为与历史一致 */
const columnConfigStorageFullKey = computed(() => {
  if (props.localStorageKey) {
    return props.localStorageKey
  }
  if (props.storageKey) {
    return `${getUserScopedStorageKey(props.storageKey)}_columns_config`
  }
  console.warn('[ColumnConfigDialog] 请提供 localStorageKey 或 storageKey')
  return 'column_config_dialog_fallback'
})

// 监听props变化
watch(() => props.show, (newVal) => {
  showDialog.value = newVal
  if (newVal) {
    loadColumnConfig()
  }
})

// 与父组件 show 双向同步：用遮罩/X 关闭时也要 emit，否则 show 一直为 true，下次点「列设置」无法再次打开
watch(
  showDialog,
  (val) => {
    if (val !== props.show) {
      emit('update:show', val)
    }
  }
)

watch(() => props.columnConfig, (newVal) => {
  localColumnConfig.value = [...newVal]
}, { deep: true })

// 加载列配置
const loadColumnConfig = () => {
  try {
    const savedConfig = localStorage.getItem(columnConfigStorageFullKey.value)
    if (savedConfig) {
      const parsedConfig = JSON.parse(savedConfig)
      // 优先使用保存的配置顺序，然后合并默认配置的属性
      const savedProps = new Set(parsedConfig.map(col => col.prop))
      
      // 使用保存的配置顺序
      localColumnConfig.value = parsedConfig.map(savedCol => {
        const defaultCol = props.columnConfig.find(col => col.prop === savedCol.prop)
        // 如果默认配置中有这个列，合并属性（保存的配置优先）
        return defaultCol ? { ...defaultCol, ...savedCol } : savedCol
      })
      
      // 添加默认配置中存在但保存配置中不存在的列（新列）
      props.columnConfig.forEach(defaultCol => {
        if (!savedProps.has(defaultCol.prop)) {
          localColumnConfig.value.push(defaultCol)
        }
      })
    } else {
      // 如果没有保存的配置，使用默认配置
      localColumnConfig.value = [...props.columnConfig]
    }
  } catch (error) {
    console.error('加载列配置失败:', error)
    // 出错时使用默认配置
    localColumnConfig.value = [...props.columnConfig]
  }
}

// 保存列配置到localStorage
const saveColumnConfig = () => {
  try {
    localStorage.setItem(columnConfigStorageFullKey.value, JSON.stringify(localColumnConfig.value))
  } catch (error) {
    console.error('保存列配置失败:', error)
  }
}

// 事件处理
const handleResetWidths = () => {
  localColumnConfig.value.forEach(col => {
    const defaultCol = props.columnConfig.find(c => c.prop === col.prop)
    if (defaultCol) {
      col.width = defaultCol.width
    }
  })
  saveColumnConfig()
  emit('config-change', [...localColumnConfig.value])
  emit('reset-widths')
  ElMessage.success('列宽已重置为默认值')
}

const handleResetAll = () => {
  localColumnConfig.value = [...props.columnConfig]
  saveColumnConfig()
  emit('config-change', [...localColumnConfig.value])
  emit('reset-all')
  ElMessage.success('所有列设置已重置为默认值')
}

const handleSelectAll = () => {
  localColumnConfig.value.forEach(col => {
    col.visible = true
  })
  saveColumnConfig()
  emit('config-change', [...localColumnConfig.value])
  emit('select-all')
  ElMessage.success('已选择所有列')
}

const handleUnselectAll = () => {
  localColumnConfig.value.forEach(col => {
    if (col.prop !== 'operations') { // 保持操作列始终显示
      col.visible = false
    }
  })
  saveColumnConfig()
  emit('config-change', [...localColumnConfig.value])
  emit('unselect-all')
  ElMessage.success('已取消选择所有列（操作列除外）')
}

const handleColumnChange = () => {
  saveColumnConfig()
  emit('config-change', [...localColumnConfig.value])
}

// eslint-disable-next-line no-unused-vars
const handleWidthChange = (column) => {
  saveColumnConfig()
  emit('config-change', [...localColumnConfig.value])
}

const handleDragEnd = () => {
  saveColumnConfig()
  emit('config-change', [...localColumnConfig.value])
}

const handleCancel = () => {
  showDialog.value = false
  emit('update:show', false)
}

const handleSave = () => {
  saveColumnConfig()
  emit('config-change', [...localColumnConfig.value])
  emit('save', [...localColumnConfig.value])
  showDialog.value = false
  emit('update:show', false)
  ElMessage.success('列设置已保存')
}
</script>

<style scoped>
/* 使用扁平化标准 CSS，勿写 SCSS 嵌套：本文件未用 lang=scss，嵌套在部分环境会成无效 CSS 导致整表样式被忽略（表现为只有文字、无复选/输入框/拖拽） */
.column-config-dialog .dialog-content {
  /* min-height:0 便于旧 WebKit/壳浏览器下 flex 子项可收缩；避免列表区被算成 0 高度 */
  min-height: 0;
  max-height: 70vh;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
}

.column-config-dialog .config-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid #e8e8e8;
  flex-shrink: 0;
}

.column-config-dialog .config-header .header-left h3 {
  margin: 0;
  color: #303133;
}

.column-config-dialog .config-header .header-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.column-config-dialog .column-list-container {
  min-height: 200px;
  max-height: 300px;
  overflow-y: auto;
  overflow-x: auto;
  margin-bottom: 20px;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  padding: 10px;
  flex: 0 1 auto;
}

.column-config-dialog .column-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 15px;
  margin-bottom: 8px;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  background-color: #fafafa;
  transition: background-color 0.2s ease, border-color 0.2s ease;
}

.column-config-dialog .column-item:hover {
  background-color: #f0f0f0;
  border-color: #c0c4cc;
}

.column-config-dialog .column-item.column-hidden {
  background-color: #f5f5f5;
  opacity: 0.7;
}

.column-config-dialog .column-controls {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1 1 40%;
  min-width: 0;
}

.column-config-dialog .column-width-control {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 15px;
  flex: 0 0 auto;
}

.column-config-dialog .column-width-control .width-unit {
  color: #909399;
  font-size: 12px;
  min-width: 25px;
}

.column-config-dialog .drag-handle {
  cursor: move;
  color: #909399;
  font-size: 16px;
  flex-shrink: 0;
}

.column-config-dialog .drag-handle:hover {
  color: #409eff;
}

.column-config-dialog .drag-handle-placeholder {
  display: inline-block;
  width: 16px;
  flex-shrink: 0;
}

.column-config-dialog .visibility-icon {
  color: #67c23a;
  font-size: 16px;
  flex-shrink: 0;
}

.column-config-dialog .visibility-icon.hidden {
  color: #f56c6c;
}

.column-config-dialog .config-preview {
  margin-bottom: 20px;
  flex-shrink: 0;
}

.column-config-dialog .config-preview h4 {
  margin: 0 0 10px 0;
  color: #606266;
}

.column-config-dialog .config-preview .preview-table {
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  overflow-x: auto;
  overflow-y: hidden;
  max-width: 100%;
}

.column-config-dialog .config-preview .preview-header {
  display: flex;
  background-color: #f5f7fa;
  border-bottom: 1px solid #e8e8e8;
}

.column-config-dialog .config-preview .preview-row {
  display: flex;
}

.column-config-dialog .config-preview .preview-column,
.column-config-dialog .config-preview .preview-cell {
  box-sizing: border-box;
  padding: 8px 12px;
  border-right: 1px solid #e8e8e8;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.column-config-dialog .config-preview .preview-column {
  font-weight: 600;
  color: #303133;
}

.column-config-dialog .config-preview .preview-column:last-child,
.column-config-dialog .config-preview .preview-cell:last-child {
  border-right: none;
}

.column-config-dialog .config-preview .preview-cell {
  color: #606266;
}

.column-config-dialog .config-tips {
  margin-top: 10px;
  flex-shrink: 0;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

/* 保障 Element 控件在窄视口下也能布局（部分壳浏览器上 flex 子项会叠成一列无间距） */
.column-config-dialog :deep(.column-controls .el-checkbox) {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  width: 100%;
  min-width: 0;
  margin-right: 0;
}

.column-config-dialog :deep(.el-input-number) {
  min-width: 100px;
}
</style>